import React, { useEffect, useState } from "react";
import { api } from "../../api/client.js";
import { useResponsiveMode } from "../shared/useResponsiveMode.js";
import { CompanyUnifiedAuth } from "./CompanyUnifiedAuth.js";
import { type AuthConfig } from "./CompanyAuthFields.js";
import { demoAssets } from "../shared/constants.js";


function LocalAuthScreen({ onAuthed, sessionNotice }: { onAuthed: () => void; sessionNotice?: string }) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState("");
  const switchMode = (nextMode: "login" | "register") => {
    setMode(nextMode);
    setError("");
  };
  const authErrorMessage = (message: string) => ({
    "invalid invite code": "邀请码不正确",
    "password confirmation does not match": "两次输入的密码不一致",
    "invalid username or password": "用户名需为 3-40 位字母、数字或符号，密码至少 8 位",
    "username already exists": "用户名已存在",
    "invalid credentials": "用户名或密码不正确"
  })[message] || message;
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    if (mode === "register" && password !== passwordConfirm) {
      setError("两次输入的密码不一致");
      return;
    }
    try {
      await api(mode === "login" ? "/api/auth/login" : "/api/auth/register", {
        method: "POST",
        body: JSON.stringify(
          mode === "login"
            ? { username, password }
            : { username, password, passwordConfirm, displayName, inviteCode }
        )
      });
      onAuthed();
    } catch (err) {
      setError(err instanceof Error ? authErrorMessage(err.message) : "登录失败");
    }
  };
  return (
    <main className="auth-shell">
      <form className="auth-panel" onSubmit={submit}>
        <div className="auth-heading">
          <img className="auth-logo" src={demoAssets.mark} alt="" />
          <div>
            <h1>Truth Seeker 科研助理</h1>
            <p>登录后进入科研问答工作台。</p>
          </div>
        </div>
        {sessionNotice && <p className="auth-session-notice" role="status">{sessionNotice}</p>}
        <div className="auth-tabs">
          <button type="button" className={mode === "login" ? "active" : ""} onClick={() => switchMode("login")}>登录</button>
          <button type="button" className={mode === "register" ? "active" : ""} onClick={() => switchMode("register")}>注册</button>
        </div>
        <label>
          用户名
          <input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" />
        </label>
        {mode === "register" && (
          <label>
            显示名
            <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="name" />
          </label>
        )}
        {mode === "register" && (
          <label>
            邀请码
            <input value={inviteCode} onChange={(event) => setInviteCode(event.target.value)} autoComplete="off" />
          </label>
        )}
        <label>
          密码
          <input value={password} type="password" onChange={(event) => setPassword(event.target.value)} autoComplete={mode === "login" ? "current-password" : "new-password"} />
        </label>
        {mode === "register" && (
          <label>
            确认密码
            <input value={passwordConfirm} type="password" onChange={(event) => setPasswordConfirm(event.target.value)} autoComplete="new-password" />
          </label>
        )}
        {error && <p className="auth-error" role="alert">{error}</p>}
        <button className="primary-action" type="submit">{mode === "login" ? "进入工作台" : "创建账号"}</button>
      </form>
    </main>
  );
}

export function AuthScreen({ onAuthed, sessionNotice, configRequest }: { onAuthed: () => void; sessionNotice?: string; configRequest?: Promise<AuthConfig> }) {
  const responsiveMode = useResponsiveMode();
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (configRequest || api<AuthConfig>("/api/auth/config"))
      .then((value) => active && setConfig(value))
      .catch(() => active && setError("认证服务配置暂时不可用，请稍后刷新。"));
    return () => { active = false; };
  }, [configRequest]);

  if (error) return <main className="boot-screen" role="alert">{error}</main>;
  if (!config) return <main className="boot-screen">Truth Seeker</main>;
  return config.mode === "company"
    ? <CompanyUnifiedAuth mobile={responsiveMode === "touch"} config={config} onAuthed={onAuthed} sessionNotice={sessionNotice} />
    : <LocalAuthScreen onAuthed={onAuthed} sessionNotice={sessionNotice} />;
}
